import { redirect } from "@tanstack/react-router";
import { clsx, type ClassValue } from "clsx";
import { jwtDecode } from "jwt-decode";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import { SuperUserPermissionsEnum } from "@/client-admin";
import { ADMIN_PORTAL_NAV_ITEMS } from "@/components/Sidebar/AdminAppSidebar";
import { CLIENT_PORTAL_NAV_ITEMS } from "@/components/Sidebar/ClientAppSidebar";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const PERMISSIONS = [
  {
    id: "clientsheet:read",
    label: "Clientsheet Read",
  },
  {
    id: "pricesheet:read",
    label: "Pricesheet Read",
  },
  {
    id: "invoice:read",
    label: "Invoice Read",
  },
  {
    id: "sparkspread:read",
    label: "SparkSpread Read",
  },
  {
    id: "price_update:write",
    label: "Price Update Write",
  },
] as const;

export const permissionSchema = z.enum(
  PERMISSIONS.map((p) => p.id) as [
    "clientsheet:read",
    "pricesheet:read",
    "invoice:read",
    "sparkspread:read",
    "price_update:write",
  ],
);

export const clientCredentialPermissionSchema = z.enum([
  "invoice:read",
  "sparkspread:read",
  "clientsheet:read",
  "price_update:write",
]);

export const SUPERUSER_PERMISSIONS = [
  {
    id: "user-management",
    label: "User Management",
  },
  {
    id: "price-update",
    label: "Price Update",
  },
  {
    id: "auto-trader",
    label: "Auto Trader",
  },
  {
    id: "algo-trading",
    label: "Algo Trading",
  },
  {
    id: "superuser-management",
    label: "Superuser Management",
  },
] as const;

export const superUserPermissionSchema = z.enum(
  SUPERUSER_PERMISSIONS.map((p) => p.id) as [
    "user-management",
    "price-update",
    "auto-trader",
    "algo-trading",
    "superuser-management",
  ],
);

export type Permission = z.infer<typeof permissionSchema>;

export const permissionsSchema = z.array(permissionSchema);
export type Permissions = z.infer<typeof permissionsSchema>;
export const superUserPermissionsSchema = z.array(superUserPermissionSchema);
export type SuperUserPermissions = z.infer<typeof superUserPermissionsSchema>;

export const tokenSchema = z.object({
  exp: z.number(),
  sub: z.string(),
  client_id: z.number(),
  permissions: z.array(z.union([permissionSchema, superUserPermissionSchema])),
  is_superuser: z.boolean(),
  auth_flow: z.literal("passwordless"),
});
export type Token = z.infer<typeof tokenSchema>;

export function checkPermission(requiredPermission: Permission): void {
  const token = getDecodedTokenFromLocalStorage();

  const currentTimeInSeconds = Math.floor(Date.now() / 1000);

  if (token.exp < currentTimeInSeconds) {
    console.info("Token expired");
    localStorage.removeItem("access_token");
    throw redirect({ to: "/login" });
  }

  if (!token.permissions.includes(requiredPermission)) {
    const isSuperUser = checkIsSuperuser();
    if (isSuperUser) {
      throw redirect({ to: "/admin/users" });
    }
    throw redirect({ to: `/403` });
  }

  return;
}

export function checkSuperUserPermission(requiredPermission: SuperUserPermissionsEnum): void {
  const isSuperUser = checkIsSuperuser();

  if (!isSuperUser) {
    throw redirect({ to: "/403" });
  }

  const token = getDecodedTokenFromLocalStorage();

  const currentTimeInSeconds = Math.floor(Date.now() / 1000);

  if (token.exp < currentTimeInSeconds) {
    console.info("Token expired");
    localStorage.removeItem("access_token");
    throw redirect({ to: "/login" });
  }

  if (token.permissions.includes(requiredPermission)) {
    return;
  }

  throw redirect({ to: `/403` });
}

function getDecodedTokenFromLocalStorage() {
  const access_token = localStorage.getItem("access_token");
  if (!access_token || typeof access_token !== "string") {
    throw redirect({ to: "/login" });
  }
  try {
    return tokenSchema.parse(jwtDecode(access_token));
  } catch (error) {
    localStorage.removeItem("access_token");
    throw redirect({ to: "/login" });
  }
}

export function getRedirectPath(decodedToken?: Token) {
  try {
    const token = decodedToken ?? getDecodedTokenFromLocalStorage();

    const isSuperUser = checkIsSuperuser();
    if (isSuperUser) {
      for (const navItem of ADMIN_PORTAL_NAV_ITEMS) {
        if (token.permissions.includes(navItem.permission)) {
          return navItem.url;
        }
      }
    }

    for (const navItem of CLIENT_PORTAL_NAV_ITEMS) {
      if (token.permissions.includes(navItem.permission)) {
        return navItem.url;
      }
    }

    return "/clientsheets";
  } catch (error) {
    localStorage.removeItem("access_token");
    return "/login";
  }
}

export function checkIsSuperuser() {
  try {
    const access_token = localStorage.getItem("access_token");
    if (!access_token || typeof access_token !== "string") {
      return false;
    }

    const token = tokenSchema.parse(jwtDecode(access_token));
    if (token.is_superuser) {
      localStorage.setItem("language", "en");
    }
    return token.is_superuser;
  } catch (error) {
    return false;
  }
}

function getAvailablePermissionsFromToken(token: Token) {
  return PERMISSIONS.filter((p) => token.permissions.includes(p.id));
}

export function getAvailablePermissionsFromLocalStorage() {
  const token = getDecodedTokenFromLocalStorage();
  return getAvailablePermissionsFromToken(token);
}

export function formatBytes(
  bytes: number,
  opts: {
    decimals?: number;
    sizeType?: "accurate" | "normal";
  } = {},
) {
  const { decimals = 0, sizeType = "normal" } = opts;

  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const accurateSizes = ["Bytes", "KiB", "MiB", "GiB", "TiB"];
  if (bytes === 0) return "0 Byte";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(decimals)} ${
    sizeType === "accurate" ? (accurateSizes[i] ?? "Bytes") : (sizes[i] ?? "Bytes")
  }`;
}

export const dateSchema = z.string().date();

export const SUPPORTED_LANGUAGES = ["nl", "en"] as const;
export const getBrowserLanguage = (): (typeof SUPPORTED_LANGUAGES)[number] => {
  const browserLang = navigator.language.split("-")[0];
  const supportedLangs = SUPPORTED_LANGUAGES;
  return supportedLangs.includes(browserLang as (typeof supportedLangs)[number])
    ? (browserLang as (typeof supportedLangs)[number])
    : "nl";
};

export const getPreferredLanguage = (): "en" | "nl" => {
  const language = localStorage.getItem("language");

  if (language && SUPPORTED_LANGUAGES.includes(language as (typeof SUPPORTED_LANGUAGES)[number])) {
    return language as (typeof SUPPORTED_LANGUAGES)[number];
  }

  return getBrowserLanguage();
};

export const getFileNameFromFilePath = (filePath: string) => {
  try {
    return filePath.split("/").pop();
  } catch (error) {
    return filePath;
  }
};

export const getErrorDetails = (error: unknown, consoleError = false) => {
  if (consoleError) {
    console.error(error);
  }
  let errorMessage = "An unknown error occurred. Please try again later.";

  if (error && typeof error === "object") {
    const err = error as { body?: { detail?: string }; message?: string };
    errorMessage = err?.body?.detail || err?.message || errorMessage;
  }

  return errorMessage;
};

export function isTokenExpired(): boolean {
  try {
    const access_token = localStorage.getItem("access_token");
    if (!access_token || typeof access_token !== "string") {
      return true;
    }

    const token = tokenSchema.parse(jwtDecode(access_token));
    const currentTimeInSeconds = Math.floor(Date.now() / 1000);

    return token.exp < currentTimeInSeconds + 60;
  } catch (error) {
    console.error("Error checking token expiration:", error);
    return true;
  }
}
